import type { ApolloErrorInterop } from '@confluence/apollo-relay-interop/entry-points/ApolloErrorInterop.ts';
import { isUnauthorizedError } from '@confluence/error-boundary/entry-points/isUnauthorizedError.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { ONBOARDING_TUTORIAL_LABEL } from '../../constants/onboarding-state/onboarding-editor-constants.ts';

import type {
	OnboardingLabelsQuery as OnboardingLabelsQueryType,
	OnboardingLabelsQueryVariables as OnboardingLabelsQueryVariablesType,
} from './__types__/LabelsQuery.ts';
import EditorTemplateLabelsQueryNode from './__generated__/EditorTemplateLabelsQueryQuery.graphql.ts';

type useCheckIfOnboardingEditorTemplateProps = {
	contentId: string;
	skip?: boolean;
};

type useCheckIfOnboardingEditorTemplatePropsReturn = {
	isTemplate: boolean;
	isOnboardingTemplate: boolean;
	labelsData: OnboardingLabelsQueryType | undefined;
	labelsLoading: boolean;
	labelsError: ApolloErrorInterop | undefined;
};

export const useCheckIfOnboardingEditorTemplate = ({
	contentId,
	skip,
}: useCheckIfOnboardingEditorTemplateProps): useCheckIfOnboardingEditorTemplatePropsReturn => {
	const {
		data: labelsData,
		loading: labelsLoading,
		error: labelsError,
	} = useQueryInterop<OnboardingLabelsQueryType, OnboardingLabelsQueryVariablesType>(
		EditorTemplateLabelsQueryNode,
		{
			variables: {
				contentId,
				status: ['current', 'draft'],
			},
			skip,
		},
	);

	const labels = labelsData?.content?.nodes?.[0]?.labels?.nodes || [];
	const sourceTemplateEntityId =
		labelsData?.content?.nodes?.[0]?.metadata?.sourceTemplateEntityId || undefined;

	// If the current editor is NOT the onboarding editor tutorial, show the draft banner.
	const isOnboardingTemplate = Boolean(
		labels.find((label) => label?.label === ONBOARDING_TUTORIAL_LABEL),
	);

	if (labelsError && isUnauthorizedError(labelsError)) {
		markErrorAsHandled(labelsError);
	}

	return {
		isTemplate: !!sourceTemplateEntityId,
		isOnboardingTemplate,
		labelsData,
		labelsLoading,
		labelsError,
	};
};
